import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import {
  BarcodeFormat,
  BarcodeScanner,
} from "@capacitor-mlkit/barcode-scanning";

/** Wird am <body> gesetzt, solange die Kamera laeuft. */
const SCANNING_CLASS = "barcode-scanning-active";

/** Das Kamerabild im Browser; nativ liegt es hinter der WebView. */
const VIDEO_CLASS = "barcode-scan-video";

/** Pause zwischen zwei Erkennungsversuchen im Browser. */
const WEB_DETECT_INTERVAL_MS = 250;

/** Kamera-Scan fuer den Gutscheincode: nativ ML Kit, im Browser getUserMedia + ZXing-WASM. */
@Injectable({
  providedIn: "root",
})
export class BarcodeScanService {
  private _running = false;
  private _settle: ((value: string | null) => void) | null = null;
  private _stream: MediaStream | null = null;
  private _video: HTMLVideoElement | null = null;

  /** Im Browser nur mit Kamera-API, also nur ueber HTTPS oder localhost. */
  public isAvailable(): boolean {
    return (
      Capacitor.isNativePlatform() ||
      typeof navigator.mediaDevices?.getUserMedia === "function"
    );
  }

  public get running(): boolean {
    return this._running;
  }

  /**
   * Startet die Kamera und liefert den ersten erkannten Code.
   *
   * @return der Code, oder null wenn abgebrochen bzw. nicht erlaubt.
   */
  public async scan(): Promise<string | null> {
    if (!this.isAvailable() || this._running) {
      return null;
    }

    return Capacitor.isNativePlatform() ? this.scanNative() : this.scanWeb();
  }

  /** Bricht einen laufenden Scan ab, z. B. ueber den Abbrechen-Knopf. */
  public async cancel(): Promise<void> {
    if (this._settle) {
      this._settle(null);
    } else if (this._running) {
      await this.stop();
    }
  }

  private async scanNative(): Promise<string | null> {
    const { supported } = await BarcodeScanner.isSupported();
    if (!supported) {
      return null;
    }

    const { camera } = await BarcodeScanner.requestPermissions();
    if (camera !== "granted" && camera !== "limited") {
      return null;
    }

    this.start();

    try {
      return await this.firstBarcodeNative();
    } finally {
      // ⚠ Auch bei Fehler und Abbruch: sonst bleibt die App durchsichtig.
      await this.stop();
    }
  }

  private async scanWeb(): Promise<string | null> {
    this.start();

    try {
      // ⚠ Erst hier geladen: das WASM-Modul ist rund 1 MB gross.
      const { BarcodeDetector, prepareZXingModule } =
        await import("barcode-detector/ponyfill");

      // ⚠ Ohne Override laedt ZXing das WASM von jsDelivr; angular.json kopiert es nach wasm/.
      prepareZXingModule({
        overrides: {
          locateFile: (path: string, prefix: string) =>
            path.endsWith(".wasm")
              ? new URL(`wasm/${path}`, document.baseURI).href
              : prefix + path,
        },
      });

      try {
        this._stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch {
        // Abgelehnt oder keine Kamera: dann bleibt die Eingabe von Hand.
        return null;
      }

      // ⚠ Abbruch waehrend der Kamera-Freigabe: stop() lief schon, der Stream kam danach.
      if (!this._running) {
        return null;
      }

      const video = document.createElement("video");
      video.className = VIDEO_CLASS;
      video.muted = true;
      // ⚠ Ohne playsinline oeffnet iOS-Safari das Video im Vollbild-Player.
      video.setAttribute("playsinline", "");
      video.srcObject = this._stream;
      document.body.appendChild(video);
      this._video = video;
      await video.play();

      const detector = new BarcodeDetector({
        formats: ["qr_code", "code_128", "code_39", "ean_13", "data_matrix"],
      });

      return await this.firstBarcodeWeb(video, detector);
    } finally {
      await this.stop();
    }
  }

  private start(): void {
    this._running = true;
    // ⚠ Schaltet die App unsichtbar, damit das Kamerabild durchscheint — stop() muss sie wieder entfernen.
    document.body.classList.add(SCANNING_CLASS);
  }

  private async stop(): Promise<void> {
    this._running = false;
    this._settle = null;
    document.body.classList.remove(SCANNING_CLASS);

    if (!Capacitor.isNativePlatform()) {
      this._stream?.getTracks().forEach((track) => track.stop());
      this._stream = null;
      this._video?.remove();
      this._video = null;
      return;
    }

    await BarcodeScanner.removeAllListeners();
    await BarcodeScanner.stopScan();
  }

  /** Haengt den Abbruch an das Ergebnis; nur der erste Wert zaehlt. */
  private settleOnce(
    resolve: (value: string | null) => void,
  ): (value: string | null) => void {
    let settled = false;
    const done = (value: string | null) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    this._settle = done;
    return done;
  }

  /**
   * ⚠ Nur Formate, die auf Gutscheinen vorkommen — je weniger Formate, desto
   * treffsicherer erkennt ML Kit.
   */
  private async firstBarcodeNative(): Promise<string | null> {
    let done!: (value: string | null) => void;
    const result = new Promise<string | null>((resolve) => {
      done = this.settleOnce(resolve);
    });

    await BarcodeScanner.addListener("barcodesScanned", (event) => {
      const value = event.barcodes?.[0]?.rawValue;
      if (value) {
        done(value);
      }
    });

    await BarcodeScanner.addListener("scanError", () => done(null));

    await BarcodeScanner.startScan({
      formats: [
        BarcodeFormat.QrCode,
        BarcodeFormat.Code128,
        BarcodeFormat.Code39,
        BarcodeFormat.Ean13,
        BarcodeFormat.DataMatrix,
      ],
    });

    return result;
  }

  /** Prueft das laufende Kamerabild, bis ein Code erkannt oder abgebrochen wird. */
  private firstBarcodeWeb(
    video: HTMLVideoElement,
    detector: {
      detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
    },
  ): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const done = this.settleOnce((value) => {
        clearTimeout(timer);
        resolve(value);
      });

      const tick = async () => {
        if (!this._running) {
          return;
        }

        try {
          const value = (await detector.detect(video))[0]?.rawValue;
          if (value) {
            done(value);
            return;
          }
        } catch {
          // Das Bild ist noch nicht bereit — beim naechsten Mal wieder.
        }

        if (this._running) {
          timer = setTimeout(tick, WEB_DETECT_INTERVAL_MS);
        }
      };

      tick();
    });
  }
}
